// Password gate and loading/error states for the admin dashboard.
import { useState, type FormEvent } from 'react';
import { Lock, LogOut, RefreshCw } from 'lucide-react';
import { BrandMark } from '../../../components/ui/bits';
import { cn } from '../../../lib/cn';
import { fetchAdminDashboard, isWrongPasswordError } from '../../../lib/adminApi';

export function AdminSplash() {
  return (
    <div className="grid h-full w-full place-items-center bg-canvas">
      <div className="flex items-center gap-3 text-ink2">
        <RefreshCw size={18} className="animate-spin" />
        <span className="text-[14px] font-semibold">Loading dashboard…</span>
      </div>
    </div>
  );
}

export function AdminError({ onRetry, onLock }: { onRetry: () => void; onLock: () => void }) {
  return (
    <div className="grid h-full w-full place-items-center bg-canvas px-6">
      <div className="w-full max-w-[380px] rounded-3xl border border-black/5 bg-card p-7 text-center shadow-card">
        <div className="grid h-12 w-12 mx-auto place-items-center rounded-full bg-miss/15 text-danger">
          <Lock size={22} />
        </div>
        <h1 className="mt-4 text-[20px] font-extrabold tracking-tight">Couldn’t load the dashboard</h1>
        <p className="mt-1 text-[13px] text-ink2">
          The password was accepted but the server hit an error building the snapshot. Check the
          <span className="font-semibold"> adminDashboard</span> function logs.
        </p>
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={onRetry}
            className="tap flex flex-1 items-center justify-center gap-2 rounded-2xl bg-night py-3 text-[14px] font-bold text-white"
          >
            <RefreshCw size={16} /> Retry
          </button>
          <button
            type="button"
            onClick={onLock}
            className="tap flex items-center justify-center gap-2 rounded-2xl border border-black/10 px-4 py-3 text-[14px] font-semibold text-ink2"
          >
            <LogOut size={16} /> Lock
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminLogin({ onUnlock }: { onUnlock: (password: string) => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      await fetchAdminDashboard(password);
      onUnlock(password);
    } catch (err) {
      setError(
        isWrongPasswordError(err)
          ? 'Wrong password. Try again.'
          : 'Server error loading the dashboard. Check the function logs and try again.'
      );
      setBusy(false);
    }
  }

  return (
    <div className="grid h-full w-full place-items-center bg-canvas px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-[380px] rounded-3xl border border-black/5 bg-card p-7 shadow-card"
      >
        <div className="flex flex-col items-center text-center">
          <BrandMark size={48} />
          <h1 className="mt-4 text-[22px] font-extrabold tracking-tight">Admin dashboard</h1>
          <p className="mt-1 text-[13px] text-ink2">Enter the password to continue.</p>
        </div>

        <div className="mt-6">
          <div
            className={cn(
              'flex items-center gap-2 rounded-2xl border bg-wash px-3.5 py-3 transition-colors',
              error ? 'border-danger/50' : 'border-black/10 focus-within:border-accent/60'
            )}
          >
            <Lock size={17} className="text-ink3" />
            <input
              type="password"
              value={password}
              autoFocus
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError(null);
              }}
              placeholder="Password"
              className="w-full bg-transparent text-[15px] font-medium text-ink outline-none placeholder:text-ink3"
            />
          </div>
          {error && <p className="mt-2 px-1 text-[13px] font-medium text-danger">{error}</p>}
        </div>

        <button
          type="submit"
          disabled={busy || !password}
          className="tap mt-5 flex w-full items-center justify-center rounded-2xl bg-night py-3.5 text-[15px] font-bold text-white disabled:opacity-40"
        >
          {busy ? 'Checking…' : 'Unlock'}
        </button>
      </form>
    </div>
  );
}
